import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'

const JOB_DATE_KEYS = [
  'requested_completion',
  'estimated_sample_delivery',
  'estimated_start',
  'actual_sample_delivery',
  'estimated_completion',
  'actual_start',
  'actual_completion',
]

const SOFT_CAP = 5

function humanize(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function tokenLabel(token) {
  if (token.type === 'job_date') return humanize(token.key)
  if (token.type === 'job_field') return `Field: ${token.match}`
  if (token.type === 'next_task_notification') return 'Next task notification'
  return JSON.stringify(token)
}

function tokenKey(token, idx) {
  return `${token.type}-${token.key || token.match || ''}-${idx}`
}

function ColumnFieldsConfigModal({ column, onClose, onSave }) {
  const initialConfig = Array.isArray(column.card_field_config) ? column.card_field_config : []
  const [tokens, setTokens] = useState(initialConfig)
  const [jobFieldInput, setJobFieldInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const namesQuery = useQuery({
    queryKey: ['jobFieldDistinctNames'],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const req = new FetchRequest('get', '/job_fields/distinct_names', {
        headers: { Accept: 'application/json' }
      })
      const r = await req.perform()
      return r.json
    }
  })

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const dateInUse = (key) => tokens.some((t) => t.type === 'job_date' && t.key === key)
  const nextNotifInUse = tokens.some((t) => t.type === 'next_task_notification')
  const exceededCap = tokens.length > SOFT_CAP

  const addDate = (key) => {
    if (dateInUse(key)) return
    setTokens([...tokens, { type: 'job_date', key }])
  }

  const addJobField = () => {
    const match = jobFieldInput.trim()
    if (!match) return
    const dupe = tokens.some((t) => t.type === 'job_field' && t.match.toLowerCase() === match.toLowerCase())
    if (dupe) {
      setJobFieldInput('')
      return
    }
    setTokens([...tokens, { type: 'job_field', match }])
    setJobFieldInput('')
  }

  const addNextNotification = () => {
    if (nextNotifInUse) return
    setTokens([...tokens, { type: 'next_task_notification' }])
  }

  const removeAt = (idx) => setTokens(tokens.filter((_, i) => i !== idx))

  const moveToken = (from, to) => {
    if (from === to) return
    const next = [...tokens]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    setTokens(next)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave(tokens)
      onClose()
    } catch (e) {
      setError(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const names = namesQuery.data?.names || []

  return (
    <div className="kb-modal kb-fc fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="kb-fc-panel" onClick={(e) => e.stopPropagation()}>
        <div className="kb-fc-header">
          <div>
            <h3 className="kb-fc-title">Card fields &mdash; {column.name}</h3>
            <div className="kb-fc-sub">Choose what shows on each card in this column.</div>
          </div>
          <button className="kb-fc-close" onClick={onClose} title="Close">&times;</button>
        </div>

        <div className="kb-fc-body">
          <div className="kb-fc-left">
            <div className="kb-fc-section">
              <div className="kb-fc-section-eyebrow">Job Dates</div>
              <div className="kb-fc-chips">
                {JOB_DATE_KEYS.map((key) => (
                  <button
                    key={key}
                    className={`kb-fc-chip ${dateInUse(key) ? 'is-used' : ''}`}
                    onClick={() => addDate(key)}
                    disabled={dateInUse(key)}
                  >
                    + {humanize(key)}
                  </button>
                ))}
              </div>
            </div>

            <div className="kb-fc-section">
              <div className="kb-fc-section-eyebrow">Job Field by Name</div>
              <div className="kb-fc-jobfield-row">
                <input
                  list="kb-fc-job-fields"
                  className="kb-fc-input"
                  placeholder="Type or pick a field name…"
                  value={jobFieldInput}
                  onChange={(e) => setJobFieldInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addJobField() } }}
                />
                <datalist id="kb-fc-job-fields">
                  {names.map((n) => <option key={n} value={n} />)}
                </datalist>
                <button
                  className="kb-fc-add"
                  onClick={addJobField}
                  disabled={!jobFieldInput.trim()}
                >
                  Add
                </button>
              </div>
              <div className="kb-fc-hint">
                Exact match, case-insensitive. Pick from the dropdown or type any name.
              </div>
            </div>

            <div className="kb-fc-section">
              <div className="kb-fc-section-eyebrow">Other</div>
              <button
                className={`kb-fc-chip ${nextNotifInUse ? 'is-used' : ''}`}
                onClick={addNextNotification}
                disabled={nextNotifInUse}
              >
                + Next task notification
              </button>
            </div>
          </div>

          <div className="kb-fc-divider" />

          <div className="kb-fc-right">
            <div className="kb-fc-section-eyebrow">Shown on cards &middot; drag to reorder</div>
            {tokens.length === 0 ? (
              <div className="kb-fc-empty">
                No extras yet &mdash; cards in this column will show only status, title, job&nbsp;ID, and tags.
              </div>
            ) : (
              <div className="kb-fc-tokens">
                {tokens.map((token, idx) => (
                  <TokenRow
                    key={tokenKey(token, idx)}
                    token={token}
                    index={idx}
                    onRemove={() => removeAt(idx)}
                    onMove={moveToken}
                  />
                ))}
              </div>
            )}
            {exceededCap && (
              <div className="kb-fc-warning">
                {tokens.length} fields selected &mdash; cards get crowded past ~{SOFT_CAP}.
              </div>
            )}
          </div>
        </div>

        <div className="kb-fc-footer">
          {error && <div className="kb-fc-error">{error}</div>}
          <div className="kb-fc-footer-actions">
            <button className="kb-fc-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              className="kb-fc-btn kb-fc-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function TokenRow({ token, index, onRemove, onMove }) {
  const rowRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState(null)

  useEffect(() => {
    const el = rowRef.current
    if (!el) return

    const cleanup = combine(
      draggable({
        element: el,
        getInitialData: () => ({ type: 'fc-token', index }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: el,
        getData: ({ input, element }) =>
          attachClosestEdge({ type: 'fc-token', index }, {
            input, element, allowedEdges: ['top', 'bottom']
          }),
        canDrop: ({ source }) => source.data.type === 'fc-token' && source.data.index !== index,
        onDragEnter: (args) => setClosestEdge(extractClosestEdge(args.self.data)),
        onDrag: (args) => setClosestEdge(extractClosestEdge(args.self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: (args) => {
          setClosestEdge(null)
          const from = args.source.data.index
          const edge = extractClosestEdge(args.self.data)
          let to = index
          if (edge === 'bottom') to = index + 1
          if (from < to) to -= 1
          onMove(from, to)
        }
      })
    )
    return cleanup
  }, [index, onMove])

  return (
    <div
      ref={rowRef}
      className={`kb-fc-token ${isDragging ? 'is-dragging' : ''} ${closestEdge ? `is-drop-${closestEdge}` : ''}`}
    >
      <span className="kb-fc-token-handle" title="Drag to reorder">⋮⋮</span>
      <span className="kb-fc-token-label">{tokenLabel(token)}</span>
      <button className="kb-fc-token-remove" onClick={onRemove} title="Remove">&times;</button>
    </div>
  )
}

ColumnFieldsConfigModal.propTypes = {
  column: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default ColumnFieldsConfigModal
